/**
 * Типы источника (документа в базе знаний).
 * Синхронизированы с backend `richat_backend.schemas.knowledge`.
 */

/**
 * Статус одного источника (Literal в schemas/knowledge.py).
 *
 * Жизненный цикл:
 *   pending_confirm → in_queue → in_progress → success / partial_success / fail
 *
 * Если юзер не вызвал /confirm в течение 24ч — cron удаляет source и S3-файл,
 * и status больше не виден (его уже нет в БД).
 */
export type SourceStatus =
  | 'pending_confirm'
  | 'in_queue'
  | 'in_progress'
  | 'success'
  | 'partial_success'
  | 'fail';

/** Терминальные статусы — на которых polling останавливается. */
export const TERMINAL_SOURCE_STATUSES: SourceStatus[] = [
  'success',
  'partial_success',
  'fail',
];

export function isTerminalStatus(status: SourceStatus): boolean {
  return TERMINAL_SOURCE_STATUSES.includes(status);
}

/** Элемент списка из GET /knowledge/sources. */
export interface SourceListItem {
  source_id: string;
  name: string | null;
  status: SourceStatus;
  process_images: boolean;
  confirm_expires_at: string | null;
  created_at: string;
}

/**
 * Ответ фазы 1 загрузки — POST /knowledge/upload (без auto_confirm).
 *
 * Юзер видит цены и решает: подтвердить (с тумблером Vision) или отменить.
 */
export interface UploadEstimate {
  source_id: string;
  assistant_id: string;
  status: 'pending_confirm';
  file_name: string | null;
  file_size_bytes: number;
  page_count: number;
  images_count: number;
  estimated_cost_with_vision_rub: number;
  estimated_cost_without_vision_rub: number;
  current_balance_rub: number;
  confirm_expires_at: string;
}

/** Ответ фазы 2 — POST /knowledge/sources/{id}/confirm. */
export interface UploadConfirmResponse {
  source_id: string;
  assistant_id: string;
  status: 'in_queue';
}

/** Ответ GET /knowledge/ingestion/{source_id}. */
export interface IngestionStatusResponse {
  source_id: string;
  status: SourceStatus;
  assistant_ingestion_status:
    | 'pending'
    | 'processing'
    | 'ready'
    | 'failed'
    | null;
  created_at: string;
}

// ===========================================================================
//  Парсер сайтов (M-web1) — типы scrape-флоу
// ===========================================================================

/**
 * Ответ POST /knowledge/scrape/estimate — старт сканирования.
 * Бэк создаёт source, ставит задачу парсеру, отвечает сразу.
 * Дальше фронт поллит GET /knowledge/scrape/estimate/{source_id}.
 */
export interface ScrapeEstimateStart {
  source_id: string;
  status: 'in_progress';
}

/** Причины фатальной ошибки scout-фазы. Каждая → своё человеческое сообщение. */
export type ScrapeEstimateErrorReason =
  | 'no_sitemap'
  | 'cloudflare_blocked'
  | 'invalid_url'
  | 'timeout'
  | 'internal_error';

/** Контакты отдела продаж для больших сайтов (too_large=true). */
export interface ScrapeContacts {
  email: string;
  telegram: string;
  message: string;
}

/**
 * Ответ GET /knowledge/scrape/estimate/{source_id}.
 *
 * Discriminated union по полю `status`:
 *  - 'in_progress' — scout ещё идёт, фронт продолжает поллить
 *  - 'ready' — превью готово, можно показать юзеру и предложить confirm
 *  - 'failed' — scout упал, показать ошибку с пояснением
 */
export type ScrapeEstimateResult =
  | ScrapeEstimateInProgress
  | ScrapeEstimateReady
  | ScrapeEstimateFailed;

export interface ScrapeEstimateInProgress {
  source_id: string;
  assistant_id: string;
  status: 'in_progress';
  process_images: boolean;
}

export interface ScrapeEstimateReady {
  source_id: string;
  assistant_id: string;
  status: 'ready';
  url: string;
  site_title: string | null;
  total_pages_in_sitemap: number;
  pages_to_process: number;
  estimated_total_images: number;
  /** Стоимости приходят как строки (Decimal на бэке), парсим в Number на UI. */
  estimated_cost_text_rub: string;
  estimated_cost_images_rub: string;
  estimated_cost_total_rub: string;
  current_balance_rub: string;
  process_images: boolean;
  too_large: boolean;
  contacts: ScrapeContacts | null;
}

export interface ScrapeEstimateFailed {
  source_id: string;
  assistant_id: string;
  status: 'failed';
  url: string;
  error: string;
  error_reason: ScrapeEstimateErrorReason;
  process_images: boolean;
}

/** Ответ POST /knowledge/scrape/confirm — запуск парсинга. */
export interface ScrapeConfirmResponse {
  source_id: string;
  pages_to_process: number;
  held_amount_rub: string;
}

/**
 * Прогресс парсинга — GET /knowledge/scrape/{source_id}/progress.
 *
 * Источник данных — Redis-счётчики, TTL 24 часа. После TTL — 404,
 * и фронт должен брать данные из БД через GET /knowledge/sources.
 *
 * ВАЖНО про images_total: эта цифра «плавает» по ходу парсинга — пока
 * страницы не обработаны, мы не знаем сколько на них уникальных контентных
 * картинок. Поэтому в UI **не показываем знаменатель для картинок** — только
 * счётчик «обработано N». Для прогресс-бара картинок не используем.
 */
export interface ScrapeProgress {
  source_id: string;
  source_status: SourceStatus;
  progress_status: 'scraping' | 'done' | 'failed';
  vision_status: 'vision_processing' | 'vision_done';
  pages_total: number;
  pages_done: number;
  pages_percent: number;
  images_total: number;
  images_done: number;
  /** Картинки с реальным caption'ом — за них юзер платит, остальные NONE. */
  images_kept: number;
  images_percent: number;
  /** Грубая оценка времени до завершения, в секундах. null в самом начале. */
  eta_seconds: number | null;
  started_at: number;
  finished_at: number | null;
}

// ===========================================================================
//  Стоимость обработки (P4) — billing summary
// ===========================================================================

/** Одна строка из разбивки стоимости обработки источника. */
export interface BillingSummaryItem {
  /** Идентификатор фазы биллинга на бэке (page_ingest, image_ingest, ...). */
  phase: string;
  /** Человеческая метка для UI ("Текст", "Картинки"). */
  label: string;
  /** Количество единиц (страниц / картинок / чанков). */
  unit_count: number;
  /** Метка единицы измерения ("страниц", "шт", "чанков"). */
  unit_label: string;
  /** Цена за единицу как строка (Decimal на бэке). */
  unit_price_rub: string;
  /** Итоговая сумма этой строки как строка (Decimal на бэке). */
  amount_rub: string;
}

/**
 * Ответ GET /knowledge/sources/{source_id}/billing-summary.
 *
 * Содержит ТОЛЬКО реально списанные деньги — hold/refund не показываются.
 * Если source ещё в обработке — total_rub и items отражают текущее состояние
 * (могут быть неполными). После vision_done — финальное состояние.
 *
 * Для fail-источника может быть `total_rub: "0.00"` и `items: []` (всё refund'нулось).
 */
export interface BillingSummary {
  source_id: string;
  total_rub: string;
  items: BillingSummaryItem[];
}

// ===========================================================================
//  Список обработанных страниц (P4-fix) — для UI «по мере обработки»
// ===========================================================================

/**
 * Одна обработанная страница веб-источника.
 *
 * Источник данных — knowledge_chunks с уникальным source_url. Появляется
 * в выдаче после того как парсер успешно завершил обработку этого URL и
 * закоммитил чанки в БД.
 */
export interface SourcePageItem {
  url: string;
  chunks_count: number;
  /** ISO datetime — когда первый чанк страницы был создан. */
  first_seen_at: string;
}

/**
 * Ответ GET /knowledge/sources/{source_id}/pages.
 *
 * Список отсортирован по first_seen_at ASC — порядок появления страниц
 * по мере обработки парсером.
 *
 * Для PDF/DOCX источников pages всегда пустой (source_url = NULL).
 */
export interface SourcePagesResponse {
  source_id: string;
  pages: SourcePageItem[];
  total_processed: number;
}
